import { BrandKey } from '@prismamedia/one-brandkey';
import {
  DraftPluginC2c,
  FramePlugin,
  FrameType,
  ImagePlugin,
  MediaPlugin,
  PageBreakPlugin,
  PastePlugin,
} from '@prismamedia/one-components';
import { searchImageSourceCategories } from '../../../apollo/sharedQueries/getSourcesCategories.web.graphql';
import { MultimediaPlugin } from '../../../components/Draft/plugins/multimedia-plugin';
import { TablePlugin } from '../../../components/Draft/plugins/table';
import { InternalLinkType } from '../../../components/InternalLinkModal/InternalLink';
import { ceriseBrandkeys } from '../../../utils/brands';
import { CERISE_ENDPOINT } from '../../../utils/cerise';
import { embedImage, uploadAndEmbedImage } from '../../../utils/upload';

export const imagePlugin = (brandKey: BrandKey, withErrors?: boolean) =>
  ImagePlugin({
    handleEmbed: (file: File) =>
      uploadAndEmbedImage(file, brandKey, { withFileName: true }),
    maxUploadSize: config.IMAGE_MAX_SIZE,
    fetchSources: searchImageSourceCategories,
    withErrors,
  });

const forbiddenURLMediaList = [CERISE_ENDPOINT];
const mediaPlugin = MediaPlugin({
  handleEmbed: embedImage,
  forbiddenURLMediaList,
});

const c2cPlugin = () => {
  return DraftPluginC2c({
    initialForm: {
      renderingOptions: [
        {
          id: 'lien-c2c',
          value: 'lien-c2c',
          label: 'Lien C2C',
        },
      ],
      rendering: 'lien-c2c',
      buttonLabel: '',
      tagId: '',
      url: '',
      noFollow: false,
      newTab: false,
    },
  });
};

const framePlugin = ({
  brandKey,
  frameOptions = [
    FrameType.NOTICE,
    FrameType.DEFAULT,
    FrameType.DEONTOLOGY,
    FrameType.SUMMARY,
    FrameType.SOURCE,
    FrameType.VERDICT,
  ],
  defaultOption = FrameType.DEFAULT,
}: {
  brandKey: BrandKey;
  frameOptions?: FrameType[];
  defaultOption?: FrameType;
}) => {
  return FramePlugin({
    frameOptions,
    defaultOption,
    plugins: [
      mediaPlugin,
      imagePlugin(brandKey),
      PastePlugin(),
      MultimediaPlugin,
    ],
  });
};
const DAILYMOTION_VIDEO_INPUT = {
  VIDEO: [
    'title',
    'medias',
    'tags',
    'event',
    'channel',
    'source',
    'visibility',
  ],
};

const ceriseConfigs = {} as Record<string, any>;

for (const ceriseBrandkey of ceriseBrandkeys) {
  ceriseConfigs[ceriseBrandkey] = {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
      'slug',
      'source',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(ceriseBrandkey),
      MultimediaPlugin,
      framePlugin({ brandKey: ceriseBrandkey }),
    ].filter(Boolean),
  };
}

export const INPUT_TPL: Record<string, any> = {
  BIN: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'secondaryTitle',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
      'source',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.BIN),
      MultimediaPlugin,
    ],
  },
  TEL: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'secondCategory',
      'source',
      'tags',
      'authors',
      'event',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.TEL),
      MultimediaPlugin,
      c2cPlugin(),
    ],
  },
  CAP: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
      'event',
      'source',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.CAP),
      MultimediaPlugin,
      PageBreakPlugin,
      c2cPlugin(),
      framePlugin({ brandKey: BrandKey.CAP }),
    ],
  },
  CAM: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'secondaryTitle',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
      'source',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.CAM),
      MultimediaPlugin,
      c2cPlugin(),
    ],
  },
  VOI: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'secondaryTitle',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'secondCategory',
      'tags',
      'authors',
      'event',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.VOI),
      MultimediaPlugin,
      c2cPlugin(),
    ],
  },
  GAL: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'secondaryTitle',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'secondCategory',
      'tags',
      'authors',
      'event',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.GAL),
      MultimediaPlugin,
    ],
  },
  HBZ: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'secondaryTitle',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'secondCategory',
      'tags',
      'authors',
      'event',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.HBZ),
      MultimediaPlugin,
      PageBreakPlugin,
      framePlugin({ brandKey: BrandKey.HBZ }),
    ],
  },
  TLS: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'secondCategory',
      'tags',
      'authors',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.TLS),
      MultimediaPlugin,
      c2cPlugin(),
    ],
    AVAILABLE_INTERNAL_LINK_TYPES: [
      InternalLinkType.ARTICLE,
      InternalLinkType.PROGRAM,
      InternalLinkType.BROADCAST,
    ],
  },
  TGC: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'secondCategory',
      'tags',
      'authors',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.TGC),
      MultimediaPlugin,
      c2cPlugin(),
    ],
    AVAILABLE_INTERNAL_LINK_TYPES: [
      InternalLinkType.ARTICLE,
      InternalLinkType.PROGRAM,
      InternalLinkType.BROADCAST,
    ],
  },
  TLA: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'secondaryTitle',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'secondCategory',
      'tags',
      'authors',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.TLA),
      MultimediaPlugin,
    ],
  },
  BEA: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.BEA),
      MultimediaPlugin,
    ],
  },
  FAC: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'secondaryTitle',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
      'event',
      'source',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.FAC),
      MultimediaPlugin,
      PageBreakPlugin,
      TablePlugin,
      framePlugin({ brandKey: BrandKey.FAC }),
      c2cPlugin(),
    ],
  },
  SHGFAC: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'secondaryTitle',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
      'event',
      'source',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.FAC),
      MultimediaPlugin,
      PageBreakPlugin,
      TablePlugin,
      framePlugin({ brandKey: BrandKey.FAC }),
      c2cPlugin(),
    ],
  },
  CAC: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
      'event',
      'source',
      'guides',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.CAC),
      MultimediaPlugin,
      PageBreakPlugin,
      TablePlugin,
    ],
  },
  GEO: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
      'source',
      'event',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.GEO),
      MultimediaPlugin,
      c2cPlugin(),
      framePlugin({ brandKey: BrandKey.GEO }),
    ],
  },
  TESTBRAND: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'secondCategory',
      'tags',
      'authors',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.TESTBRAND),
      MultimediaPlugin,
    ],
    AVAILABLE_INTERNAL_LINK_TYPES: [
      InternalLinkType.ARTICLE,
      InternalLinkType.PROGRAM,
      InternalLinkType.BROADCAST,
    ],
  },
  UKSAS: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
      'event',
      'source',
      'guides',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.UKSAS),
      MultimediaPlugin,
    ],
  },
  DESAS: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
      'event',
      'source',
      'guides',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.DESAS),
      MultimediaPlugin,
    ],
  },
  NEO: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'secondaryTitle',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
      'source',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.NEO),
      MultimediaPlugin,
    ],
  },
  ...ceriseConfigs,
  HBR: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'secondaryTitle',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
      'slug',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.HBR),
      MultimediaPlugin,
      framePlugin({ brandKey: BrandKey.HBR }),
    ],
  },
  SIM: {
    ...DAILYMOTION_VIDEO_INPUT,
    RICH: [
      'publishedAt',
      'title',
      'secondaryTitle',
      'partnerTitle',
      'medias',
      'lightDraft',
      'richDraft',
      'firstCategory',
      'tags',
      'authors',
    ],
    RICH_DRAFT_PLUGINS: [
      mediaPlugin,
      imagePlugin(BrandKey.SIM),
      MultimediaPlugin,
    ],
  },
};
